import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';
import { Card, CardHeader, Badge, Button } from '@tkpod/ui';
import { Link } from 'react-router';
import {
  ORDER_STATUS_MERCHANT_VARIANT,
  getMerchantOrderStatusLabel,
  type PortalDashboardStats,
} from '@tkpod/shared';
import { OrderTrendChart } from '../components/dashboard/OrderTrendChart';

function sumStatusCounts(
  byStatus: PortalDashboardStats['orders']['byStatus'],
  statuses: string[],
): number {
  return byStatus
    .filter((s) => statuses.includes(s.status))
    .reduce((sum, s) => sum + s.count, 0);
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} padding="md">
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-24 rounded bg-surface-tertiary" />
              <div className="h-8 w-16 rounded bg-surface-tertiary" />
            </div>
          </Card>
        ))}
      </div>
      <Card padding="md">
        <div className="animate-pulse space-y-3">
          <div className="h-5 w-36 rounded bg-surface-tertiary" />
          <div className="h-[180px] w-full rounded bg-surface-tertiary sm:h-[200px]" />
        </div>
      </Card>
      <Card>
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-40 rounded bg-surface-tertiary" />
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-4 w-20 rounded bg-surface-tertiary" />
                  <div className="h-5 w-24 rounded-full bg-surface-tertiary" />
                </div>
                <div className="flex items-center gap-6">
                  <div className="h-4 w-16 rounded bg-surface-tertiary" />
                  <div className="h-4 w-24 rounded bg-surface-tertiary" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {['Total Orders', 'This Month', 'Pending', 'In Production'].map((label) => (
          <Card key={label} padding="md">
            <p className="text-sm text-text-secondary">{label}</p>
            <p className="mt-1 text-2xl font-semibold text-text-primary">0</p>
          </Card>
        ))}
      </div>
      <Card>
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <h3 className="text-lg font-semibold text-text-primary">No orders yet</h3>
          <p className="text-sm text-text-secondary">
            Start by creating your first order to see stats here.
          </p>
          <Link to="/orders/new">
            <Button variant="primary" size="sm" className="mt-2">
              Create your first order
            </Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}

export function DashboardPage() {
  const api = useApiClient();

  const statsQuery = useQuery({
    queryKey: ['portal', 'dashboard', 'stats'],
    queryFn: () =>
      api.get<{ data: PortalDashboardStats }>('/api/v1/portal/dashboard/stats'),
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  if (statsQuery.isLoading) {
    return <DashboardSkeleton />;
  }

  if (statsQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <h2 className="text-xl font-semibold text-text-primary">
          Failed to load dashboard
        </h2>
        <p className="text-sm text-text-secondary">
          Something went wrong while fetching your dashboard data.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void statsQuery.refetch()}
        >
          Try again
        </Button>
      </div>
    );
  }

  const stats = statsQuery.data?.data;

  if (!stats || stats.orders.total === 0) {
    return <EmptyState />;
  }

  const { orders, dailyTrend, recentOrders } = stats;
  const pendingCount = sumStatusCounts(orders.byStatus, [
    'NEW',
    'DESIGN_PENDING',
  ]);
  // Merchant lifecycle terms: "Order Processing" covers the export/claim
  // stages, "In Production" is READY_TO_SHIP (label purchased, presses
  // starting) — this KPI counts BOTH so actively-worked orders never vanish
  // from the dashboard, and its label matches the badge wording.
  const inProgressCount = sumStatusCounts(orders.byStatus, [
    'EXPORT_QUEUED',
    'EXPORT_PROCESSING',
    'EXPORTED',
    'IN_PRODUCTION',
    'PRODUCTION_DONE',
    'READY_TO_SHIP',
  ]);

  const kpis = [
    { label: 'Total Orders', value: orders.total },
    { label: 'This Month', value: orders.thisMonth },
    { label: 'Pending', value: pendingCount },
    { label: 'In Progress', value: inProgressCount },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {kpis.map((kpi) => (
          <Card key={kpi.label} padding="md">
            <p className="text-sm text-text-secondary">{kpi.label}</p>
            <p className="mt-1 text-2xl font-semibold text-text-primary">
              {kpi.value}
            </p>
          </Card>
        ))}
      </div>

      {dailyTrend && dailyTrend.length > 0 && (
        <Card padding="md">
          <p className="mb-3 text-sm font-medium text-text-secondary">
            Order Trend — Last 30 Days
          </p>
          <OrderTrendChart data={dailyTrend} />
        </Card>
      )}

      <Card>
        <CardHeader
          title="Recent Orders"
          action={
            <Link to="/orders">
              <Button variant="outline" size="sm">
                View all
              </Button>
            </Link>
          }
        />
        <div className="-mx-6 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-secondary/40">
                <th className="px-6 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                  Order
                </th>
                <th className="px-6 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                  Status
                </th>
                <th className="px-6 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                  Total
                </th>
                <th className="hidden px-6 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary sm:table-cell">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {recentOrders.map((o) => (
                <tr
                  key={o.id}
                  className="transition-colors hover:bg-surface-secondary/60"
                >
                  <td className="px-6 py-2.5 whitespace-nowrap">
                    <Link
                      to={`/orders/${o.id}`}
                      className="font-medium text-brand-600 hover:underline"
                    >
                      {o.orderNumber}
                    </Link>
                  </td>
                  <td className="px-6 py-2.5 whitespace-nowrap">
                    <Badge
                      variant={ORDER_STATUS_MERCHANT_VARIANT[o.status] ?? 'default'}
                      size="sm"
                    >
                      {getMerchantOrderStatusLabel(o.status)}
                    </Badge>
                  </td>
                  <td className="px-6 py-2.5 text-right whitespace-nowrap font-medium text-text-primary">
                    ${o.total}
                  </td>
                  <td className="hidden px-6 py-2.5 whitespace-nowrap text-[12px] text-text-tertiary sm:table-cell">
                    {new Date(o.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
